import { ProjectStatsResponse } from './MonitorPromptsKeywordsStatsResponse';
import { getDefaultRange } from '../../../../utils/utils';
import { client } from '../../../apiClient';
import { format } from 'date-fns';
import { PeriodType } from '../../../../types/prompt/PeriodType';
import { ChatType } from '../../../../types/monitor/ChatType';
import { KEYWORDS_ENDPOINT } from '../KeywordsService';
import { KeywordsDailyStatsMultipleResponse } from '../../../../models/monitor/keywords/stats/KeywordsDailyStatsResponse';
import { PromptBrandsStatsResponse } from '../../../../models/monitor/keywords/stats/PromptBrandsStatsResponse';

export const STATS_ENDPOINT = `${KEYWORDS_ENDPOINT}/stats`;

// Per-prompt daily stats from monitor_keyword_stats (live per-answer positions). Used by the question
// stats page for Average Position (tile + chart), matching the old V1 / Explore AI Answers table.
export const getKeywordsDailyStats = async (
  monitorId: number,
  type: string,
  period?: PeriodType | 'DAILY',
  from?: Date | null,
  to?: Date | null,
  models?: string[]
): Promise<KeywordsDailyStatsMultipleResponse> => {
  const defaultRange = getDefaultRange();
  from = from ? new Date(from) : defaultRange[0];
  to = to ? new Date(to) : defaultRange[1];

  const formattedFrom = format(from, 'yyyy-MM-dd');
  const formattedTo = format(to, 'yyyy-MM-dd');

  const modelsParam = models && models.length > 0 ? `?models=${models.join(',')}` : '';

  const { data } = await client(true, true).get<KeywordsDailyStatsMultipleResponse>(
    `${STATS_ENDPOINT}/${monitorId}/${period ?? 'DAILY'}/${type}/${formattedFrom}/${formattedTo}${modelsParam}`
  );
  return data;
};

// Per-brand stats for one prompt (ranking / visibility / SoV-by-model) from monitor_keyword_stats +
// monitor_data. `models` scopes the visibility denominator (empty = every collected model).
export const getPromptBrandsStats = async (
  monitorId: number,
  period: PeriodType | 'DAILY',
  from?: Date | null,
  to?: Date | null,
  models?: string[]
): Promise<PromptBrandsStatsResponse> => {
  const defaultRange = getDefaultRange();
  from = from ? new Date(from) : defaultRange[0];
  to = to ? new Date(to) : defaultRange[1];

  const formattedFrom = format(from, 'yyyy-MM-dd');
  const formattedTo = format(to, 'yyyy-MM-dd');

  const modelsParam = models && models.length > 0 ? `?models=${models.join(',')}` : '';

  const { data } = await client(true, true).get<PromptBrandsStatsResponse>(
    `${STATS_ENDPOINT}/brands/${monitorId}/${period ?? 'DAILY'}/${formattedFrom}/${formattedTo}${modelsParam}`
  );
  return data;
};

export const getKeywordsDailyStatsForProject = async (
  projectId: number,
  chatType: ChatType,
  period?: PeriodType | 'DAILY',
  from?: Date | null,
  to?: Date | null,
  models?: string[]
): Promise<ProjectStatsResponse> => {
  const defaultRange = getDefaultRange();
  from = from ? new Date(from) : defaultRange[0];
  to = to ? new Date(to) : defaultRange[1];

  const formattedFrom = format(from, 'yyyy-MM-dd');
  const formattedTo = format(to, 'yyyy-MM-dd');

  // Scopes the server-side ads/shopping merge exactly like the old client-side features fetch did.
  const modelsParam = models && models.length > 0 ? `?models=${models.join(',')}` : '';

  const { data } = await client(true, true).get<ProjectStatsResponse>(
    `${STATS_ENDPOINT}/project/${projectId}/${chatType}/${period}/${formattedFrom}/${formattedTo}${modelsParam}`
  );
  return data;
};

export const exportPromptStats = async (
  promptId: number,
  chatType: ChatType,
  from?: Date | null,
  to?: Date | null
): Promise<Blob> => {
  const defaultRange = getDefaultRange();
  from = from ? new Date(from) : defaultRange[0];
  to = to ? new Date(to) : defaultRange[1];

  const formattedFrom = format(from, 'yyyy-MM-dd');
  const formattedTo = format(to, 'yyyy-MM-dd');

  return client(true, true)
    .get<Blob>(
      `${STATS_ENDPOINT}/export-stats/${promptId}/${formattedFrom}/${formattedTo}/${chatType}`,
      {
        responseType: 'blob',
      }
    )
    .then((response) => response.data)
    .catch((error) => error);
};
